import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';
import {normalize} from 'normalizr';
import getFacilityDetails from 'api/facilities/get-facility-details';
import getSearchedFacilities from 'api/facilities/get-searched-facilities';
import {ROUTES} from 'constants/app-routes';
import merge from 'lodash/merge';
import {canonicalFacilityEntity} from './normalization-schemas';
import {generatePath, matchPath} from 'react-router-dom';
import {flattenObjectValues} from 'utils/format-strings';
import {
    selectFacilitiesById,
    selectFilteredFacilitiesById,
} from './facilities-selectors';
import history from 'utils/history';
import Config from 'hooks/config';
import {getFilterOptions} from 'api/facilities/get-filter-options';

export const ERRORS = {
    NO_RESULTS_FOUND: 'No facilities found',
};
/**
 * Sets the search text for for the facilities search.
 *
 * @param {string} searchText - The text to be set as the search text.
 */
export const setFacilitySearchText = createAction('facilities/setSearchText');

/**
 * Action creator to set the selected facility id in the store
 * @function updateSelectedFacilityId
 */
export const updateSelectedFacilityId = createAction(
    'facilities/updateSelectedFacilityId'
);

/**
 * Thunk to set the filtered facilities in the store.  This uses a Thunk instead of an action creator for more convenient access to the store.
 *
 * Action Type: facilities/setFilteredFacilities
 */
export const setFilteredFacilities = createAsyncThunk(
    'facilities/setFilteredFacilities',
    /**
     *
     * @param {Array<number>} facilityIds - array of facility ids to set in the store
     * @param {Object} thunkAPI - The thunk API object including dispatch and getState methods, along with rejectWithValue.
     */
    (facilityIds, {dispatch, getState, rejectWithValue}) => {
        try {
            const state = getState();
            // get both the currently filtered facilities and the pending filtered facilities in the store. We need to merge these two lists to ensure that the selected facility is in the list of filtered facilities
            const pendingFilteredFacilities = selectFacilitiesById(
                state,
                facilityIds
            );
            const filteredFacilities = selectFilteredFacilitiesById(
                state,
                facilityIds
            );

            const updatedFacilities = [
                ...filteredFacilities,
                ...pendingFilteredFacilities,
            ];
            // check if the selectedFacilityId is in the updated list
            const {selectedFacilityId} = state.facilities;
            const hasSelectedFacility = updatedFacilities.some(
                (facility) => facility.id === selectedFacilityId
            );

            // when the currently selected facility is removed from the filteredFacilities list, we need to update the selectedFacilityId to the first facility in the filteredFacilities list and update the url.  This prevents an unintentional change to the currently selected facility when a user updates the filteredFacilities
            if (!hasSelectedFacility && updatedFacilities.length) {
                dispatch(updateSelectedFacilityId(updatedFacilities[0].id));

                // check if the current route matches any of our routes
                const route = state.router.location.pathname;
                const flattenedRoutes = flattenObjectValues(ROUTES);
                const matchedPath = matchPath(route, {
                    path: flattenedRoutes,
                    exact: true,
                    strict: true,
                });

                if (matchedPath) {
                    // if the current route matches one of our routes, we can use the generatePath helper to create the updated resource url & then push that new url
                    const url = generatePath(matchedPath.path, {
                        ...matchedPath.params,
                        facilityId: updatedFacilities[0].id,
                    });

                    history.push(url);
                }
            }

            if (!updatedFacilities.length) {
                dispatch(updateSelectedFacilityId(null));
            }

            return updatedFacilities;
        } catch (err) {
            console.error('setFacilities error:', err);

            return rejectWithValue(err);
        }
    }
);

/**
 * Retrieves the searched facilities data asynchronously.
 *
 * Action Type: facilities/getSearchedFacilitiesData
 *
 * API Service: {@link getSearchedFacilities}
 */
export const getPaginatedFacilitiesData = createAsyncThunk(
    'facilities/getPaginatedFacilitiesData',
    /**
     *
     * @param {Object} searchParams - The parameters used to search for facilities.
     * @param {Object} thunkAPI - The thunk API object including dispatch and getState methods, along with rejectWithValue.
     * @returns {Promise} A promise that resolves to the normalized entities of the facilities.
     * */
    async (searchParams, {rejectWithValue}) => {
        try {
            const response = await getSearchedFacilities(searchParams);
            let responseData;

            if (!response.results.length) {
                responseData = {
                    entities: {
                        canonicalFacilities: [],
                        parkingSpots: [],
                    },
                    meta: response.meta,
                };

                return responseData;
            }

            const normalizedResponse = normalize(response.results, [
                canonicalFacilityEntity,
            ]);

            responseData = {
                entities: normalizedResponse.entities,
                meta: response.meta,
            };

            return responseData;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk to get the searched facilities data.
 *
 * Action Type: facilities/getSearchedFacilitiesData
 *
 * API Service: {@link getSearchedFacilities}
 */
export const getSearchedFacilitiesData = createAsyncThunk(
    'facilities/getSearchedFacilitiesData',
    /**
     * @param {Object} searchParams - The parameters used to search for facilities.
     * @param {Object} thunkAPI - The thunk API object including dispatch and getState methods, along with rejectWithValue.
     */
    async (searchParams, {rejectWithValue}) => {
        try {
            const response = await getSearchedFacilities(searchParams);

            if (response.results.length === 0) {
                return rejectWithValue({
                    message: ERRORS.NO_RESULTS_FOUND,
                    meta: response.meta,
                });
            }

            const normalizedResponse = normalize(response.results, [
                canonicalFacilityEntity,
            ]);

            const responseData = {
                entities: normalizedResponse.entities,
                meta: response.meta,
            };

            return responseData;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk to get the details for a single facility.
 *
 *
 * Action Type: facilities/getFacilityDetails
 *
 * API Service: {@link getFacilityDetails}
 */
export const getFacilityDetailsData = createAsyncThunk(
    'facilities/getFacilityDetails',
    /**
     * @param {Object} params - The parameters to pass to the api
     * @param {number} params.facilityId - The facility id
     * @param {object} thunkAPI - The thunk API object including dispatch and getState methods, along with rejectWithValue.
     * */
    async ({facilityId}, {rejectWithValue}) => {
        try {
            const response = await getFacilityDetails(facilityId);

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk to make the initial facilities api call.  This is a paginated request.
 *
 * Action Type: facilities/getInitialFacilities
 */
export const getInitialFacilities = createAsyncThunk(
    'facilities/getInitialFacilities',
    /**
     *
     * @param {Object} params - params to pass to the api
     * @param {number} params.limit - number of facilities to get per request
     * @param {number} params.offset - number of facilities to get per request
     * @param {object} thunkAPI - The thunk API object including dispatch and getState methods, along with rejectWithValue.
     */
    async (
        {limit = 25, offset = 0} = {},
        {dispatch, rejectWithValue, getState}
    ) => {
        try {
            const state = getState();
            const match = matchPath(state.router.location.pathname, {
                path: ROUTES.facilities.facility,
            });

            const facilityIdParam =
                match && parseInt(match.params.facilityId, 10);

            let initialResponse;

            initialResponse = await dispatch(
                getPaginatedFacilitiesData({
                    filters: {status: [{label: 'On', value: 'On'}]},
                    limit,
                    offset,
                })
            ).unwrap();

            if (isEmpty(initialResponse.entities.parkingSpots)) {
                initialResponse = await dispatch(
                    getPaginatedFacilitiesData({
                        filters: {
                            status: [],
                        },
                        limit,
                        offset,
                    })
                ).unwrap();
            }

            const hasFacility = Boolean(
                initialResponse.entities.parkingSpots[facilityIdParam]
            );

            // next if the facilityId url param is present, but the facility wasn't in that initial response, we need to make a separate request to get the facility details.  If the user doesn't have permission,  we'll catch that error later.
            let facility = {};

            if (facilityIdParam && !hasFacility) {
                const facilityDetailsResponse = await dispatch(
                    getPaginatedFacilitiesData({
                        facilityIds: [facilityIdParam],
                    })
                ).unwrap();

                facility = facilityDetailsResponse.payload;
            }

            // now we merge that facility into the initial response so that it's in the store
            const updatedInitialResponse = {
                results: merge(initialResponse.entities, facility),
                meta: initialResponse.meta,
            };

            // the first facility that's selected by default should be the first facility that belongs to the first canonical facility sorted alphabetically
            const firstFacilityId = Object.values(
                updatedInitialResponse.results.canonicalFacilities
            ).sort((a, b) => a.name.localeCompare(b.name))[0]?.parkingSpots[0];

            const facilityId = facilityIdParam || firstFacilityId;

            // if the facilityId url param is present, we need to set the initial filteredFacilities in the store to just that facility otherwise, we'll use the first facility

            if (facilityId) {
                dispatch(setFilteredFacilities([facilityId]));

                // set the selectedFacilityId in the store to the first facility
                dispatch(updateSelectedFacilityId(facilityId));
            }

            return updatedInitialResponse;
        } catch (err) {
            console.error('getInitialFacilities error:', {err});
            // redirect the user if they don't have permission to view the facility. Use window.location.replace because we want to load the page and reset the state not just update the url in the browser, while also preventing the user from navigating back to the route they tried accessing.

            if (err.status === 403) {
                window.location.replace(
                    `${window.location.origin}${Config.basename}${ROUTES.dashboard}`
                );
            }

            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk to get the facility search filter options.
 *
 * Action Type: facilities/getFilterOptions
 *
 * API Service: {@link getFilterOptions}
 */
export const getFilterOptionsData = createAsyncThunk(
    'facilities/getFilterOptions',
    async ({rejectWithValue}) => {
        try {
            const response = await getFilterOptions();

            return response;
        } catch (err) {
            console.error('getFilterOptions error:', err);

            return rejectWithValue(err);
        }
    }
);

/**
 * Action creator for updating the applied filters in the facilities module.
 *
 */
export const updateAppliedFilters = createAction(
    'facilities/updateAppliedFilters'
);

/**
 * Action creator for clearing all applied filters in the facilities module.
 *
 * This action, when dispatched, triggers the removal of any filters that have been applied to the facilities list, effectively resetting the state to its default, unfiltered form.
 *
 */
export const clearAppliedFilters = createAction(
    'facilities/clearAppliedFilters'
);
